import React, { useState } from "react";
import { Link } from "react-router-dom";
import Layout from "../components/Layout";
import MetaTags from "../lib/MetaTags";
import Breadcrumbs from "../components/Breadcrumbs";
import { submitContact, formatApiErrorDetail } from "../lib/api";
import { SITE } from "../lib/categories";
import { Twitter, Facebook, Instagram } from "lucide-react";

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "" });
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(false);

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setStatus(null);
    try {
      const r = await submitContact(form);
      setStatus({ ok: true, msg: r.message });
      setForm({ name: "", email: "", subject: "", message: "" });
    } catch (err) {
      setStatus({ ok: false, msg: formatApiErrorDetail(err.response?.data?.detail) });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Layout>
      <MetaTags title="Contact" description="Get in touch with Chicago Electronics Guide. Tips, corrections, and reader questions welcome." />
      <section className="border-b border-[#121212] bg-white">
        <div className="ceg-container py-10">
          <Breadcrumbs items={[{ to: "/", label: "Home" }, { label: "Contact" }]} />
          <p className="ceg-overline mt-6">Reach the editorial team</p>
          <h1 className="font-['Outfit'] font-black tracking-tighter text-4xl sm:text-5xl md:text-6xl lg:text-7xl mt-3 leading-[0.95]">
            Send a tip.<br /><span className="text-[#0055FF]">Or just say hi.</span>
          </h1>
        </div>
      </section>

      <section className="ceg-container py-16 grid grid-cols-12 gap-10">
        <form onSubmit={submit} className="col-span-12 md:col-span-7 bg-white border border-[#121212] p-8" data-testid="contact-form">
          {status && (
            <div
              className={`mb-6 p-3 border-l-4 ${status.ok ? "border-[#0055FF] bg-blue-50" : "border-[#FF3823] bg-red-50"} text-sm`}
              data-testid="contact-status"
            >
              {status.msg}
            </div>
          )}
          <div className="grid sm:grid-cols-2 gap-5">
            <div>
              <label className="ceg-overline block mb-2">Name</label>
              <input required value={form.name} onChange={update("name")} className="w-full bg-transparent border border-[#121212] px-3 py-2 outline-none focus:border-[#0055FF]" data-testid="contact-name" />
            </div>
            <div>
              <label className="ceg-overline block mb-2">Email</label>
              <input required type="email" value={form.email} onChange={update("email")} className="w-full bg-transparent border border-[#121212] px-3 py-2 outline-none focus:border-[#0055FF]" data-testid="contact-email" />
            </div>
          </div>
          <div className="mt-5">
            <label className="ceg-overline block mb-2">Subject</label>
            <input required value={form.subject} onChange={update("subject")} className="w-full bg-transparent border border-[#121212] px-3 py-2 outline-none focus:border-[#0055FF]" data-testid="contact-subject" />
          </div>
          <div className="mt-5">
            <label className="ceg-overline block mb-2">Message</label>
            <textarea required rows={6} value={form.message} onChange={update("message")} className="w-full bg-transparent border border-[#121212] px-3 py-2 outline-none focus:border-[#0055FF] resize-none" data-testid="contact-message" />
          </div>
          <button type="submit" disabled={loading} className="ceg-btn-primary mt-6 disabled:opacity-60" data-testid="contact-submit">
            {loading ? "Sending..." : "Send message"}
          </button>
        </form>

        <aside className="col-span-12 md:col-span-5 space-y-6">
          <div className="border border-[#121212] p-6 bg-white">
            <p className="ceg-overline">Resource hub</p>
            <h3 className="font-['Outfit'] font-bold text-2xl mt-2">Recycling & Repair Directory</h3>
            <p className="text-sm text-[#5C5C5C] mt-2">Looking for where to repair, sell, donate, or recycle a device in Chicago? Browse our independent directory.</p>
            <Link to="/directory" className="mt-4 inline-block text-xs uppercase tracking-wider font-semibold underline underline-offset-4 decoration-[#0055FF]" data-testid="contact-directory-link">
              Open the directory →
            </Link>
          </div>

          <div className="bg-[#121212] text-white p-6">
            <p className="ceg-overline text-white">Follow</p>
            <h3 className="font-['Outfit'] font-bold text-2xl mt-2">On the social web</h3>
            <div className="flex gap-3 mt-4">
              <a href={SITE.social.twitter} target="_blank" rel="noopener noreferrer" className="w-10 h-10 border border-white flex items-center justify-center hover:bg-[#FF3823] hover:border-[#FF3823] transition-colors" data-testid="social-twitter"><Twitter size={16} /></a>
              <a href={SITE.social.facebook} target="_blank" rel="noopener noreferrer" className="w-10 h-10 border border-white flex items-center justify-center hover:bg-[#FF3823] hover:border-[#FF3823] transition-colors" data-testid="social-facebook"><Facebook size={16} /></a>
              <a href={SITE.social.instagram} target="_blank" rel="noopener noreferrer" className="w-10 h-10 border border-white flex items-center justify-center hover:bg-[#FF3823] hover:border-[#FF3823] transition-colors" data-testid="social-instagram"><Instagram size={16} /></a>
            </div>
          </div>
        </aside>
      </section>
    </Layout>
  );
}
